import { useContext, useMemo } from 'react';
import {
  ButtonContext as RACButtonContext,
  LabelContext as RACLabelContext,
  ListStateContext as RACListStateContext,
  Provider as RACProvider,
  TextContext as RACTextContext,
} from 'react-aria-components';
import { TagGroup } from '../tag/tag-group.js';
import { Tag, type TagProps } from '../tag/tag.js';
import type { CollectionItem, Key } from '../types.js';
import type { TagFieldProps } from './tag-field.js';

/** @internal */
export function TagFieldTags<T extends CollectionItem>({
  inputId,
  selectedKeys,
  isDisabled,
  isReadOnly,
  renderEmptyState,
  onRemove,
}: Pick<TagFieldProps<T>, 'isDisabled' | 'isReadOnly' | 'renderEmptyState'> & {
  inputId: string;
  selectedKeys: Set<Key>;
  onRemove: (key: Key) => void;
}) {
  const listState = useContext(RACListStateContext);

  // The plugin doesn't see `selectedKeys` as a legitimate dependency
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const selectedItems = useMemo(() => {
    return [...selectedKeys.values()]
      .map((key) => listState?.selectionManager.collection.getItem(key))
      .filter((value) => !!value);
  }, [selectedKeys, listState?.selectionManager.collection]);

  return (
    <RACProvider
      values={[
        // Reset contexts so that TagGroup doesn't inherit any data from RACComboBox
        [RACLabelContext, null],
        [RACButtonContext, null],
        [RACTextContext, null],
        [RACListStateContext, null],
      ]}
    >
      <TagGroup
        aria-labelledby={inputId}
        disabledKeys={isDisabled ? selectedKeys : undefined}
        renderEmptyState={renderEmptyState}
        onRemove={
          isReadOnly
            ? undefined
            : (keys) => {
                keys.forEach(onRemove);
              }
        }
      >
        {selectedItems.map((item) => (
          <Tag key={item.key} id={item.key} isDisabled={(item.props as TagProps).isDisabled}>
            {item.textValue}
          </Tag>
        ))}
      </TagGroup>
    </RACProvider>
  );
}
